import React, { useContext, useEffect, useRef, useState } from "react";
import DraftsCredentialTemplateCard from "../../components/Cards/DraftsCredentialTemplateCard/DraftsCredentialTemplateCard";
import { GET } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import AuthContext from "../../store/auth-context";
import CfLoader from "../../common/CfLoader/CfLoader";
import { useLocation, useNavigate } from "react-router-dom";
import CalculateCardsPerRow from "../../service/CalculateCardsPerRow";
import ToolTip from "../../common/ToolTip/ToolTip";
import podcastIcon from "../../resources/icons/podcast-icon.svg";
import { useTranslation } from "react-i18next";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import ArrowLeftIcon from "../../resources/icons/arrow-narrow-left.svg";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const AddDrafts = ({
  programmeId,
  programmeName,
  batchId,
  batchName,
  onClose,
}) => {
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isLoadingCredTemplates, setIsLoadingCredTemplates] = useState(false);
  const [cardWidth] = useState("19.125rem");
  const [gap] = useState("1.5rem");
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(0);
  const [countRecentList, setCountRecentList] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [recentList, setRecentList] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedTemplateData, setSelectedTemplateData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [isPaginating, setIsPaginating] = useState(false);
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isViewAllMode, setIsViewAllMode] = useState(false);

  const performSearch = async (term, signal) => {
    const endpoint = `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}/clone?batch_code=${batchId}&limit=${cardsPerRow}&offset=0&search=${term}&view_all=true`;

    const response = await GET(endpoint, { signal });

    const data = await response.json();
    if (data.error) throw data.error;

    setSearchResults(data.data.results);
    setCountRecentList(data.data.count);
    setNext(data.data.next);
    setPrevious(data.data.previous);
  };

  const { handleSearch, cleanup: cleanupSearch } = useSearchDebounce(
    performSearch,
    500,
  );

  const SEARCH = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    setOffset(0);
    setSearchCompleted(false);

    if (!term.trim()) {
      cleanupSearch();
      setIsSearching(false);
      setSearchResults([]);
      if (isViewAllMode) {
        GET_ISSUER_ALL_TEMPLATES(cardsPerRow, 0);
      } else {
        GET_ISSUER_RECENT(cardsPerRow, 0);
      }
      return;
    }

    if (cardsPerRow <= 0) {
      return;
    }
    setIsSearching(true);
    handleSearch(term, {
      minChars: 1,
      onComplete: () => setSearchCompleted(true),
      onError: () => {
        setSearchResults([]);
        setIsSearching(false);
        setSearchCompleted(true);
      },
    });
  };

  const GET_ISSUER_RECENT = (limit, offset) => {
    setIsLoadingCredTemplates(true);
    setIsPaginating(true);
    GET(
      `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}/clone?batch_code=${batchId}&limit=${limit}&offset=${offset}`,
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
        } else {
          setRecentList(data.data.results);
          setCountRecentList(data.data.count);
          setNext(data.data.next);
          setPrevious(data.data.previous);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoadingCredTemplates(false);
        setIsPaginating(false);
        setInitialLoadCompleted(true);
      });
  };

  const GET_ISSUER_ALL_TEMPLATES = (limit, offset) => {
    setIsLoadingCredTemplates(true);
    setIsPaginating(true);
    GET(
      `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}/clone?batch_code=${batchId}&limit=${limit}&offset=${offset}&view_all=true`,
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
        } else {
          setRecentList(data.data.results);
          setCountRecentList(data.data.count);
          setNext(data.data.next);
          setPrevious(data.data.previous);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoadingCredTemplates(false);
        setIsPaginating(false);
        setInitialLoadCompleted(true);
      });
  };

  const handleViewAllClick = () => {
    setIsViewAllMode(true);
    setOffset(0);
    GET_ISSUER_ALL_TEMPLATES(cardsPerRow, 0);
  };

  const handleBackClick = () => {
    setIsViewAllMode(false);
    setOffset(0);
    GET_ISSUER_RECENT(cardsPerRow, 0);
  };

  const handleTemplateSelect = (templateId, templateData = null) => {
    if (templateId === selectedTemplateId) {
      setSelectedTemplateId(null);
      setSelectedTemplateData(null);
    } else {
      setSelectedTemplateId(templateId);
      setSelectedTemplateData(templateData);
    }
  };

  const handleTemplatesClick = () => {
    navigate("/designs");
  };

  const handleAddTemplate = () => {
    if (!selectedTemplateId || !selectedTemplateData) return;

    // Store the programme and batch codes and names in localStorage for EditIssuerTemplate to use
    localStorage.setItem("programme_code", programmeId);
    localStorage.setItem("programme_name", programmeName);
    localStorage.setItem("batch_code", batchId);
    localStorage.setItem("batch_name", batchName);
    // Set flag to indicate this is a new template being added (needs clone API call on submit)
    localStorage.setItem("is_new_template_addition", "true");
    localStorage.setItem("issuer_template_id", selectedTemplateId);

    // Get the necessary data from the selected template
    const { credential_template_id, template_type } = selectedTemplateData;

    // Close the modal and navigate to EditIssuerTemplate page
    onClose();
    navigate(
      `/issuer/certificate/edit/${credential_template_id}/${selectedTemplateId}/${template_type}`,
    );
  };

  const fetchSearchPage = (offset) => {
    const endpoint = `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}/clone?batch_code=${batchId}&limit=${cardsPerRow}&offset=${offset}&search=${searchTerm}&view_all=true`;

    GET(endpoint)
      .then((res) => res.json())
      .then((data) => {
        setSearchResults(data.data.results);
        setCountRecentList(data.data.count);
        setNext(data.data.next);
        setPrevious(data.data.previous);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleNext = () => {
    const newOffset = offset + cardsPerRow;
    setOffset(newOffset);

    if (isSearching && searchTerm) {
      fetchSearchPage(newOffset);
    } else {
      if (isViewAllMode) {
        GET_ISSUER_ALL_TEMPLATES(cardsPerRow, newOffset);
      } else {
        GET_ISSUER_RECENT(cardsPerRow, newOffset);
      }
    }
  };

  const handlePrevious = () => {
    const newOffset = offset - cardsPerRow;
    if (newOffset >= 0) {
      setOffset(newOffset);

      if (isSearching && searchTerm) {
        fetchSearchPage(newOffset);
      } else {
        if (isViewAllMode) {
          GET_ISSUER_ALL_TEMPLATES(cardsPerRow, newOffset);
        } else {
          GET_ISSUER_RECENT(cardsPerRow, newOffset);
        }
      }
    }
  };

  const removeCredentialFromList = (credentialId) => {
    const removeAndEnsure = (list, setList, startDay, endDay, count) => {
      setList((prev) => {
        const updatedList = prev.filter(
          (item) => item.issuer_credential_template_id !== credentialId,
        );
        ensureFullRow(updatedList, setList, startDay, endDay, count);
        return updatedList;
      });
    };
    const ensureFullRow = (list, setList, startDay, endDay, count) => {
      if (list.length < cardsPerRow && count > list.length) {
        const offset = list.length;
        GET(
          `${EntityConstants.GET_DRAFTS_TEMPLATES}limit=${
            cardsPerRow - list.length
          }&offset=${offset}&start_day=${startDay}&end_day=${endDay}`,
        )
          .then((response) => response.json())
          .then((data) => {
            if (!data.error && data.results.length > 0) {
              setList((prev) => [...prev, ...data.results]);
            }
          })
          .catch((error) => {
            console.error("Error fetching additional certificates:", error);
          });
      }
    };
    removeAndEnsure(searchResults, setSearchResults);
    removeAndEnsure(recentList, setRecentList, 0, 7, countRecentList);
  };

  const nextButtonWithTooltip = (
    <ToolTip
      component={
        <button
          onClick={handleNext}
          disabled={next === null || selectedTemplateId}
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
        >
          <span className="flex items-center justify-center gap-2">
            <span>{t("drafts:next")}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
              />
            </svg>
          </span>
        </button>
      }
      message={
        selectedTemplateId
          ? t("drafts:deselect_certificate_to_navigate")
          : next === null
            ? t("drafts:no_next_page_available")
            : ""
      }
      tooltipPosition="top-[-4rem]"
      bgColor="#6B7280"
      tooltipWidth="w-40"
    />
  );

  const previousButtonWithTooltip = (
    <ToolTip
      component={
        <button
          onClick={handlePrevious}
          disabled={previous === null || selectedTemplateId}
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
              />
            </svg>
            <span>{t("drafts:previous")}</span>
          </span>
        </button>
      }
      message={
        selectedTemplateId
          ? t("drafts:deselect_certificate_to_navigate")
          : previous === null
            ? t("drafts:no_previous_page_available")
            : ""
      }
      tooltipPosition="top-[-4rem]"
      bgColor="#6B7280"
      tooltipWidth="w-40"
    />
  );

  const nextButtonNoTooltip = (
    <button
      id="add-drafts-pagination-next"
      onClick={handleNext}
      disabled={next === null || selectedTemplateId}
      className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
    >
      <span className="flex items-center justify-center gap-2">
        <span>{t("drafts:next")}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-5 h-5 rtl:-scale-x-100"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
          />
        </svg>
      </span>
    </button>
  );

  const previousButtonNoTooltip = (
    <button
      id="add-drafts-pagination-previous"
      onClick={handlePrevious}
      disabled={previous === null || selectedTemplateId}
      className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
    >
      <span className="flex items-center justify-center gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-5 h-5 rtl:-scale-x-100"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
          />
        </svg>
        <span>{t("drafts:previous")}</span>
      </span>
    </button>
  );

  useEffect(() => {
    const updateCardsPerRow = () => {
      if (containerRef.current) {
        const calculatedCardsPerRow = CalculateCardsPerRow(
          containerRef,
          cardWidth,
          gap,
        );
        if (calculatedCardsPerRow < 5) {
          setCardsPerRow(6);
        } else {
          setCardsPerRow(calculatedCardsPerRow);
        }
      }
    };

    updateCardsPerRow();
  }, [cardWidth, gap]);

  useEffect(() => {
    if (cardsPerRow > 0) {
      GET_ISSUER_RECENT(cardsPerRow, 0);
    }
  }, [cardsPerRow]);

  useEffect(() => {
    authContext.lastVisitedPathHandler(location.pathname);
  }, []);

  const showEmptyState =
    initialLoadCompleted &&
    !isSearching &&
    recentList.length === 0 &&
    !isLoadingCredTemplates;

  return (
    <div className="flex flex-col items-center w-full bg-cfSecondary min-h-screen" id={pageRootTestIds.addDrafts}>
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4 top-0 sticky z-20`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg">
            {!isSearching && !showEmptyState && isViewAllMode && (
              <button
                onClick={handleBackClick}
                id="add-drafts-back-button"
                className="cursor-pointer focus:outline-none"
                aria-label="Go back"
              >
                <img
                  src={ArrowLeftIcon}
                  alt="Back arrow"
                  className="cursor-pointer"
                />
              </button>
            )}
            <p>
              {isViewAllMode
                ? t("drafts:all_certificates")
                : t("drafts:existing_certificates")}
            </p>
          </div>
          <div className="flex gap-2">
            {selectedTemplateId && (
              <button
                onClick={handleAddTemplate}
                id="add-drafts-add-template"
                className="px-4 py-2 bg-cfPrimaryButton text-white font-medium rounded-md hover:bg-cfPrimaryDark transition-colors duration-200"
              >
                {t("drafts:add_certificate")}
              </button>
            )}
            {/* Search */}
            <form
              onSubmit={(e) => e.preventDefault()}
              className={`bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center md:self-end md:m-0`}
            >
              <input
                type="text"
                name="search"
                id="add-drafts-input-search"
                onChange={SEARCH}
                placeholder={t("drafts:search_certificate")}
                className="outline-none border-none p-0 placeholder:text-sm w-full"
                style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                value={searchTerm}
              />
            </form>
          </div>
        </div>
      </div>

      <div ref={containerRef} className="w-full px-4 pb-4">
        {showEmptyState && (
          <div className="flex flex-col flex-wrap sm:mt-0 xl:mt-6 w-full bg-white rounded-2xl">
            <div className="flex flex-col px-2 lg:px-0 mt-20 md:mt-36 justify-center items-center gap-5">
              <img
                alt="certificate-icon"
                src={podcastIcon}
                height="80px"
                width="80px"
                className=""
              />
              <h1 className="text-cfHeadingText font-semibold text-center">
                {t("drafts:no_certificates_available")}
              </h1>
              <h3 className="text-xs font-bold text-center lg:text-md text-gray-500">
                {t("drafts:no_certificates_created_or_issued")}
                <br />
                {t("drafts:start_by_choosing_template")}
              </h3>
              <button
                id="add-drafts-see-templates"
                className="rounded-[8px] items-center px-4 py-2.5 bg-cfPrimaryButton text-white font-medium lg:font-semibold text-sm"
                onClick={handleTemplatesClick}
              >
                {t("drafts:see_templates")}
              </button>
            </div>
          </div>
        )}

        {isSearching && (
          <div className="flex flex-col">
            <div
              className="grid w-full mt-8 gap-6 justify-items-center"
              style={{
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(var(--min-column-width), 1fr))",
              }}
            >
              {searchResults.map(
                ({
                  issuer_credential_template_id,
                  credential_template_id,
                  certificate_name,
                  thumbnail_image,
                  thumbnail_webp_url,
                  created_at,
                  updated_at,
                  description,
                  tagsList,
                  template_type,
                  programme_name,
                  batch_name,
                }) => (
                  <DraftsCredentialTemplateCard
                    key={issuer_credential_template_id}
                    name={certificate_name}
                    credential_template_id={credential_template_id}
                    issuer_credential_template_id={
                      issuer_credential_template_id
                    }
                    created_at={created_at}
                    lastEdited={updated_at}
                    thumbnail_image={
                      thumbnail_image ? thumbnail_image : thumbnail_webp_url
                    }
                    thumbnail_webp_url={thumbnail_webp_url}
                    description={description}
                    tagsList={tagsList}
                    removeCredentialFromList={removeCredentialFromList}
                    removeActionButtons={true}
                    isSelected={
                      selectedTemplateId === issuer_credential_template_id
                    }
                    onSelect={handleTemplateSelect}
                    template_type={template_type}
                    programme_name={programme_name}
                    batch_name={batch_name}
                    fullWidth={true}
                  />
                ),
              )}
              {isSearching && !searchCompleted && (
                <div className="col-span-full w-full flex justify-center items-center py-4">
                  <CfLoader />
                </div>
              )}
              {searchCompleted && isSearching && searchResults.length === 0 && (
                <div className="col-span-full mt-6 w-full flex justify-center">
                  <p
                    id="add-drafts-search-no-results"
                    className="text-lg font-semibold text-gray-600"
                  >
                    {t("drafts:certificate_not_found")}
                  </p>
                </div>
              )}
            </div>
            {searchResults.length > 0 && (
              <div className="flex items-center justify-between mt-6 w-full">
                {selectedTemplateId || previous === null
                  ? previousButtonWithTooltip
                  : previousButtonNoTooltip}
                {selectedTemplateId || next === null
                  ? nextButtonWithTooltip
                  : nextButtonNoTooltip}
              </div>
            )}
          </div>
        )}

        {!isSearching && !showEmptyState && (
          <>
            {recentList[0] && (
              <div className="flex justify-between my-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <p className="text-cfHeadingText font-medium text-lg">
                    {t("drafts:please")}{" "}
                    <span className="text-cfPrimaryButton">
                      {t("drafts:select_one")}
                    </span>{" "}
                    {t("drafts:certificate_template_to_issue")}
                  </p>
                </div>
                {!isViewAllMode && (
                  <button
                    onClick={handleViewAllClick}
                    id="add-drafts-view-all"
                    className="font-cfPrimaryFont text-sm font-semibold text-cfViewAllTagText cursor-pointer focus:outline-none hover:text-cfPrimaryButton transition-colors duration-200"
                  >
                    <p>{t("drafts:view_all")}</p>
                  </button>
                )}
              </div>
            )}
            <div className="flex flex-col">
              <div
                className="grid w-full gap-6 justify-items-center"
                style={{
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(var(--min-column-width), 1fr))",
                }}
              >
                {isPaginating ? (
                  <div className="col-span-full w-full flex justify-center items-center py-[18rem]">
                    <CfLoader />
                  </div>
                ) : (
                  <>
                    {recentList.map(
                      ({
                        issuer_credential_template_id,
                        credential_template_id,
                        certificate_name,
                        thumbnail_image,
                        thumbnail_webp_url,
                        created_at,
                        updated_at,
                        description,
                        tagsList,
                        template_type,
                        programme_name,
                        batch_name,
                      }) => (
                        <DraftsCredentialTemplateCard
                          key={issuer_credential_template_id}
                          name={certificate_name}
                          credential_template_id={credential_template_id}
                          issuer_credential_template_id={
                            issuer_credential_template_id
                          }
                          created_at={created_at}
                          //! remove this when `updated_at` is added 'lastEdited={created_at?created_at:"14 Jan 2024"}'
                          lastEdited={updated_at}
                          thumbnail_image={
                            thumbnail_image
                              ? thumbnail_image
                              : thumbnail_webp_url
                          }
                          thumbnail_webp_url={thumbnail_webp_url}
                          description={description}
                          tagsList={tagsList}
                          removeCredentialFromList={removeCredentialFromList}
                          removeActionButtons={true}
                          isSelected={
                            selectedTemplateId === issuer_credential_template_id
                          }
                          onSelect={handleTemplateSelect}
                          template_type={template_type}
                          programme_name={programme_name}
                          batch_name={batch_name}
                          fullWidth={true}
                        />
                      ),
                    )}
                    {/* Fix loader positioning - show only when loading and no certificates */}
                    {isLoadingCredTemplates && recentList.length === 0 && (
                      <div className="col-span-full w-full flex justify-center items-center py-[18rem]">
                        <CfLoader />
                      </div>
                    )}
                  </>
                )}
              </div>
              {recentList.length > 0 && (
                <div className="flex items-center justify-between mt-6 w-full">
                  {selectedTemplateId || previous === null
                    ? previousButtonWithTooltip
                    : previousButtonNoTooltip}
                  {selectedTemplateId || next === null
                    ? nextButtonWithTooltip
                    : nextButtonNoTooltip}
                </div>
              )}
            </div>
            {recentList.length === 0 &&
              !isSearching &&
              !isLoadingCredTemplates && (
                <div className="mt-6 w-full flex justify-center">
                  <p
                    id="add-drafts-no-certificates"
                    className="text-lg font-semibold text-gray-600"
                  >
                    {t("drafts:no_certificates_found")}
                  </p>
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default AddDrafts;
